import React from 'react';
import { Input } from 'reactstrap';
import {
    NonCompetitionRestrictionSlot,
    NonCompetitionRestrictionSlotLabels,
} from '../../../../../../slices/marketplace/loi-form/non-competition-restriction-slot';
import { LegalIssueItem } from '../../../../../../slices/marketplace/loi-form/legal-issue-item';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';

const NCR_LABEL_TOOLTIPS = {
    distanceRadiusMiles: 'This value is taken from your input in the legal term guidance tab, under the Asset Purchase Agreement section. Editing the value there will change the value shown here.',
    durationMonthsFromClosing: 'This value is taken from your input in the legal term guidance tab, under the Asset Purchase Agreement section. Editing the value there will change the value shown here.',
    durationMonthsFromEmployment: 'This value is taken from your input in the legal term guidance tab, under the Employment agreement section. Editing the value there will change the value shown here.',
} as const;

function getItemForSlot(
    legalIssues: LegalIssueItem[],
    additionalCovenants: LegalIssueItem[],
    slot: NonCompetitionRestrictionSlot
): LegalIssueItem | undefined {
    return (
        legalIssues.find((i) => i.linkedNonCompetitionRestrictionSlot === slot) ??
        additionalCovenants.find((i) => i.linkedNonCompetitionRestrictionSlot === slot)
    );
}

function getValueForSlot(
    legalIssues: LegalIssueItem[],
    additionalCovenants: LegalIssueItem[],
    slot: NonCompetitionRestrictionSlot
): string | number | undefined {
    const item = getItemForSlot(legalIssues, additionalCovenants, slot);
    if (!item?.value) return undefined;
    if (slot === NonCompetitionRestrictionSlot.DistanceRadiusMiles) {
        const n = Number(item.value);
        return Number.isFinite(n) ? n : item.value;
    }
    return item.value;
}

export interface TermsFormNonCompetitionBlockProps {
    legalIssues: LegalIssueItem[];
    additionalCovenants?: LegalIssueItem[];
    readOnly?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    rowComments?: (string | null)[];
    onRowCommentChange?: (rowIndex: number, comment: string | null) => void;
}

export const TermsFormNonCompetitionBlock: React.FC<
    TermsFormNonCompetitionBlockProps
> = ({
    legalIssues,
    additionalCovenants = [],
    isBuyerReview,
    isBuyerReviewConfirmed,
    rowComments,
    onRowCommentChange,
}) => {
    const showCommentColumn = isBuyerReview || !!isBuyerReviewConfirmed;

    return (
        <div className="terms-form-detail-block terms-form-section-underlined">
            <div className="terms-form-block-name">Non-Competition Restriction</div>
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            <th>
                                {NonCompetitionRestrictionSlotLabels[NonCompetitionRestrictionSlot.DistanceRadiusMiles]}
                                <InfoTooltip text={NCR_LABEL_TOOLTIPS.distanceRadiusMiles} idText="ncr-distance" />
                            </th>
                            <th>
                                {NonCompetitionRestrictionSlotLabels[NonCompetitionRestrictionSlot.DurationYearsFromClosing]}
                                <InfoTooltip text={NCR_LABEL_TOOLTIPS.durationMonthsFromClosing} idText="ncr-duration-closing" />
                            </th>
                            <th>
                                {NonCompetitionRestrictionSlotLabels[NonCompetitionRestrictionSlot.DurationYearsFromEmployment]}
                                <InfoTooltip text={NCR_LABEL_TOOLTIPS.durationMonthsFromEmployment} idText="ncr-duration-employment" />
                            </th>
                            {showCommentColumn && (
                                <th className="terms-form-col-comment">Comments</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="terms-form-col-value">
                                <Input
                                    type="number"
                                    value={
                                        getValueForSlot(
                                            legalIssues,
                                            additionalCovenants,
                                            NonCompetitionRestrictionSlot.DistanceRadiusMiles
                                        ) ?? ''
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Distance Radius Miles"
                                    disabled
                                />
                            </td>
                            <td className="terms-form-col-value">
                                <Input
                                    type="text"
                                    value={
                                        getValueForSlot(
                                            legalIssues,
                                            additionalCovenants,
                                            NonCompetitionRestrictionSlot.DurationYearsFromClosing
                                        ) ?? ''
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Duration Months from Closing"
                                    disabled
                                />
                            </td>
                            <td className="terms-form-col-value">
                                <Input
                                    type="text"
                                    value={
                                        getValueForSlot(
                                            legalIssues,
                                            additionalCovenants,
                                            NonCompetitionRestrictionSlot.DurationYearsFromEmployment
                                        ) ?? ''
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Duration Months from Employment"
                                    disabled
                                />
                            </td>
                            {showCommentColumn && (
                                <td className="terms-form-col-comment">
                                    {isBuyerReview ? (
                                        <Input
                                            type="textarea"
                                            value={rowComments?.[0] ?? ''}
                                            onChange={(e) =>
                                                onRowCommentChange?.(
                                                    0,
                                                    e.target.value === ''
                                                        ? null
                                                        : e.target.value
                                                )
                                            }
                                            className="terms-form-comment-input"
                                            aria-label="Comment for Non-Competition Restriction"
                                        />
                                    ) : rowComments?.[0] ? (
                                        <Input
                                            type="textarea"
                                            value={rowComments[0] ?? ''}
                                            disabled
                                            className="terms-form-comment-input"
                                        />
                                    ) : null}
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
